//#region IMPORTS
import { useState, useEffect } from 'react';
import { useToasts } from 'react-toast-notifications';
import { useDispatch } from 'react-redux';
import language from '../../../config/es';
import { es_vacio } from '../../../config/validate';
import { FILTER_FIELDS, createInitialFilter, getDateFormat } from './filterConfig';
//#endregion IMPORTS

/**
 * Hook con la logica comun de los monitores parciales de conciliacion.
 * @param {object} data datos del monitor (sociedad, banco, opcion, fecha)
 * @param {function} fetchAction accion redux que obtiene el listado
 * @param {object} resetFlags bandera a evaluar para limpiar cada campo al desactivar su filtro
 */
const useConciliationPartial = (data, fetchAction, resetFlags = {}) => {
  //#region VARIABLES_INIT
  const { addToast } = useToasts();
  const dispatch = useDispatch();

  const [lsConciliationDaily, setLsConciliationDaily] = useState([]);
  const [sortName, setSortName] = useState('obs');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pendingData, setPendingData] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataFilter, setDataFilter] = useState(createInitialFilter);
  const refreshDataFilter = () => {
    setDataFilter({ ...dataFilter });
  };
  //#endregion VARIABLES_INIT

  //#region FUNCTIONS
  const viewNotification = (msm, action) => {
    addToast(msm, {
      appearance: action,
      autoDismiss: true,
    });
  };
  //#endregion FUNCTIONS

  //#region LOAD_DATA
  const loadConciliationList = async (filterPage, sortColumnName, sortColumnOrder) => {
    const json = {
      filter: {
        clientName: dataFilter.clientName.value,
        clientCi: dataFilter.clientCi.value,
        clientCodeSap: dataFilter.clientCodeSap.value,
        bankCode: data.selectedBanking,
        dateIssue: data.optionConciliation === 'history' ? getDateFormat(data.dateIssue) : '',
      },
      option: data.optionConciliation,
      societyId: data.society.value,
      sortField: sortColumnName == null ? sortName : sortColumnName,
      sortOrder: sortColumnOrder == null ? sortOrder : sortColumnOrder,
      pageNumber: filterPage.page,
      pageSize: filterPage.size
    };
    setPendingData(true);
    try {
      const res = await dispatch(fetchAction(json));
      if (res.status === 200) {
        setLsConciliationDaily(res.data.entities);
        setTotalRows(res.data.totalCount);
      } else {
        switch (res.response.status) {
          case 403:
            viewNotification(res.response.data.error, 'error');
            break;
          case 404:
            viewNotification(res.response.data.error, 'warning');
            break;
          default:
            viewNotification(language.messages.msmErrorExist, 'error');
            break;
        }
        setLsConciliationDaily([]);
      }
    } catch (err) {
      console.error(err);
      viewNotification(language.messages.msmErrorExist, 'error');
    }
    setPendingData(false);
  };
  //#endregion LOAD_DATA

  //#region LIFECYCLE
  useEffect(() => {
    void loadConciliationList({ page: 1, size: perPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion LIFECYCLE

  //#region ONCHANGE_DATA
  const onChangeField = (event) => {
    const field = FILTER_FIELDS.find((item) => item.name === event.target.name);
    if (field) {
      dataFilter[field.name].value = event.target.value;
      dataFilter[field.name].error = '';
    }
    refreshDataFilter();
  };
  const onChangeSelect = (value, id) => {
    const field = FILTER_FIELDS.find((item) => item.flag === id);
    if (field) {
      dataFilter[field.flag] = value.target.checked;
      if (dataFilter[resetFlags[field.name] || field.flag] === false) {
        dataFilter[field.name].value = '';
        dataFilter[field.name].error = '';
      }
      const { filterData } = language.messages;
      if (dataFilter[field.flag] === true) {
        viewNotification(filterData.addOptionFilter.replace('{0}', field.notifyLabel), 'info');
      } else {
        viewNotification(filterData.removeOptionFilter.replace('{0}', field.notifyLabel), 'warning');
      }
    }
    refreshDataFilter();
  };
  //#endregion ONCHANGE_DATA

  //#region FUNCTIONS_FORM
  const validate = () => {
    dataFilter.errors = [];
    FILTER_FIELDS.forEach((field) => {
      dataFilter[field.name].error = '';
    });
    FILTER_FIELDS.forEach((field) => {
      if (dataFilter[field.flag] === true && es_vacio(dataFilter[field.name].value)) {
        dataFilter.errors.push(field.name + '.error');
        dataFilter[field.name].error = language.messages.warning_required_field;
      }
    });
    refreshDataFilter();
  };
  const searConciliationDaily = () => {
    validate();
    if (dataFilter.errors.length === 0) {
      setPendingData(true);
      void loadConciliationList({ page: currentPage, size: perPage }, sortName, sortOrder);
    } else {
      viewNotification(language.messages.msmErrorForm, 'error');
    }
  };
  //#endregion FUNCTIONS_FORM

  //#region FUNCTIONS_DATA_TABLE
  const handlePageChange = (page) => {
    void loadConciliationList({ page: page, size: perPage }, sortName, sortOrder);
    setCurrentPage(page);
  };
  const handlePerRowsChange = (newPerPage, page) => {
    void loadConciliationList({ page: page, size: newPerPage }, sortName, sortOrder);
    setPerPage(newPerPage);
  };
  const customSort = (field, sortDirection) => {
    void loadConciliationList({ page: currentPage, size: perPage }, field.selector, sortDirection);
    setSortName(field.selector);
    setSortOrder(sortDirection);
  };
  //#endregion FUNCTIONS_DATA_TABLE

  return {
    dataFilter,
    lsConciliationDaily,
    pendingData,
    totalRows,
    currentPage,
    onChangeField,
    onChangeSelect,
    searConciliationDaily,
    handlePageChange,
    handlePerRowsChange,
    customSort,
  };
};

export default useConciliationPartial;
